import environment from "../config/environment";
import { handleSessionExpired } from "../config/common";
import { downloadBlob } from "./recordFiles";

/**
 * Training downloads: my transcript (PDF or CSV), a course certificate, and — for a customer's
 * training admin — a colleague's transcript. All go through copilot-api /apis/copilot/lms/download,
 * which streams the file from AVS OS with headers it builds itself.
 *
 * Raw fetch, the recordFiles.ts pattern, so the axios interceptor's toasts never fire and a JSON
 * body (a refusal, or the session-expired envelope) is never mistaken for file bytes.
 */

export type LmsDownloadKind = "transcript-pdf" | "transcript-csv" | "certificate" | "admin-transcript-pdf";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NEEDS_ID: Record<LmsDownloadKind, boolean> = {
  "transcript-pdf": false,
  "transcript-csv": false,
  certificate: true,
  "admin-transcript-pdf": true,
};

/** The file types a training download may be. Anything else is saved as opaque bytes. */
const TYPES: Record<string, string> = { "application/pdf": "pdf", "text/csv": "csv" };

export function lmsDownloadUrl(kind: LmsDownloadKind, id?: string): string {
  const data = NEEDS_ID[kind] ? { kind, id } : { kind };
  return environment.BASE_URL + "copilot/lms/download?data=" + encodeURIComponent(JSON.stringify(data));
}

/**
 * The name to save under. Built from words we choose plus the date — never from upstream — and
 * only letters, digits and hyphens survive from the label (a course or person's name).
 */
export function lmsDownloadName(kind: LmsDownloadKind, label?: string | null, now = new Date()): string {
  const slug = (label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  const date = now.toISOString().slice(0, 10);
  const stem = kind === "certificate" ? "certificate" : "training-transcript";
  const ext = kind === "transcript-csv" ? "csv" : "pdf";
  return `${[stem, slug].filter(Boolean).join("-")}-${date}.${ext}`;
}

export type LmsFileResult = { ok: true; blob: Blob } | { ok: false; reason: "session" | "failed"; message?: string };

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function fetchLmsFile(kind: LmsDownloadKind, id?: string): Promise<LmsFileResult> {
  if (!(kind in NEEDS_ID)) return { ok: false, reason: "failed" };
  if (NEEDS_ID[kind] && !(typeof id === "string" && UUID_RE.test(id))) return { ok: false, reason: "failed" };

  let res: Response;
  try {
    res = await fetch(lmsDownloadUrl(kind, id), {
      method: "GET",
      credentials: "include",
      headers: { Authorization: "Bearer " + environment.BEARER_TOKEN },
    });
  } catch {
    return { ok: false, reason: "failed" };
  }

  try {
    const contentType = (res.headers.get("content-type") || "").toLowerCase();
    if (contentType.includes("application/json")) {
      const body = safeJson(await res.text());
      if (body?.status === 401) {
        handleSessionExpired();
        return { ok: false, reason: "session" };
      }
      return { ok: false, reason: "failed", message: typeof body?.message === "string" ? body.message : undefined };
    }
    if (res.status === 401) {
      handleSessionExpired();
      return { ok: false, reason: "session" };
    }
    if (!res.ok) return { ok: false, reason: "failed" };
    const base = contentType.split(";")[0].trim();
    const blob = await res.blob();
    return { ok: true, blob: new Blob([blob], { type: TYPES[base] ? base : "application/octet-stream" }) };
  } catch {
    return { ok: false, reason: "failed" };
  }
}

/** Fetch and save. Resolves to an error message for the page, or null when the file was saved. */
export async function saveLmsFile(kind: LmsDownloadKind, id?: string, label?: string | null): Promise<string | null> {
  const r = await fetchLmsFile(kind, id);
  if ("blob" in r) {
    downloadBlob(r.blob, lmsDownloadName(kind, label));
    return null;
  }
  if (r.reason === "session") return null;
  return r.message || "That file couldn't be downloaded, please try again.";
}
